import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Award, Plus, X, Pencil, Trash2,
  Search, Filter, Building2, Briefcase, Calendar, ChevronDown, ChevronUp, ChevronRight
} from 'lucide-react';

const ACCENT = '#667eea';
const TEXT_TITLE = '#1a1a2e';
const TEXT_BODY = '#334155';
const TEXT_MUTED = '#64748b';
const TEXT_FAINT = '#94a3b8';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';
const SURFACE_MUTED = '#f8fafc';
const ACCENT_TINT = '#f0f4ff';

function formatDeliveryDate(iso) {
  if (!iso) return '';
  const [y, m, day] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return '';
  const d = new Date(y, m - 1, day);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthKey(iso) {
  return String(iso).slice(0, 7);
}

function monthLabel(key) {
  const [y, m] = key.split('-');
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

function weekStart(iso) {
  const ymd = String(iso).slice(0, 10);
  const d = new Date(ymd + 'T00:00:00');
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function weekKey(iso) {
  const start = weekStart(iso);
  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, '0');
  const day = String(start.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function weekLabel(key) {
  const start = new Date(key + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = { month: 'short', day: 'numeric' };
  return `Week of ${start.toLocaleDateString(undefined, fmt)} – ${end.toLocaleDateString(undefined, fmt)}`;
}

function groupEntries(entries, groupBy) {
  const map = new Map();
  for (const e of entries) {
    let key, label;
    if (groupBy === 'month') {
      key = monthKey(e.delivery_date);
      label = monthLabel(key);
    } else if (groupBy === 'week') {
      key = weekKey(e.delivery_date);
      label = weekLabel(key);
    } else {
      key = `c-${e.client_id}`;
      label = e.client_name;
    }
    if (!map.has(key)) map.set(key, { key, label, entries: [] });
    map.get(key).entries.push(e);
  }
  const groups = Array.from(map.values());
  if (groupBy === 'client') {
    groups.sort((a, b) => a.label.localeCompare(b.label));
  } else {
    groups.sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
  }
  return groups;
}

const fieldStyle = (active) => ({
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: active ? `2px solid ${ACCENT}` : `1px solid ${BORDER}`,
  background: active ? ACCENT_TINT : SURFACE,
  fontSize: '0.9rem',
  color: TEXT_BODY,
  outline: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit'
});

const filterLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: TEXT_MUTED,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '6px'
};

function Journal({ user, workspaceId }) {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [groupBy, setGroupBy] = useState('month');
  const [collapsed, setCollapsed] = useState(new Set());

  useEffect(() => {
    loadEntries();
    axios.get('/api/people-clients').then((r) => setClients(r.data)).catch(() => {});
    axios.get('/api/projects').then((r) => setProjects(r.data)).catch(() => {});
  }, [workspaceId]);

  const loadEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/journal');
      setEntries(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load journal');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => setEditing(entry);

  const handleSaved = (entry) => {
    setEntries((prev) => {
      const idx = prev.findIndex((p) => p.id === entry.id);
      if (idx === -1) return [entry, ...prev];
      const next = [...prev];
      next[idx] = entry;
      return next;
    });
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/journal/${entry.id}`);
      setEntries((prev) => prev.filter((p) => p.id !== entry.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return entries.filter((e) => {
      if (q && !(e.title.toLowerCase().includes(q) || e.description.toLowerCase().includes(q))) return false;
      if (filterClientId && e.client_id !== parseInt(filterClientId, 10)) return false;
      if (filterProjectId && e.project_id !== parseInt(filterProjectId, 10)) return false;
      const entryDate = String(e.delivery_date).slice(0, 10);
      if (filterFrom && entryDate < filterFrom) return false;
      if (filterTo && entryDate > filterTo) return false;
      return true;
    });
  }, [entries, searchQuery, filterClientId, filterProjectId, filterFrom, filterTo]);

  const activeFilterCount =
    (filterClientId ? 1 : 0) +
    (filterProjectId ? 1 : 0) +
    (filterFrom ? 1 : 0) +
    (filterTo ? 1 : 0);
  const anyFilterActive = Boolean(searchQuery || activeFilterCount);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterClientId('');
    setFilterProjectId('');
    setFilterFrom('');
    setFilterTo('');
  };

  const groups = useMemo(() => groupEntries(filteredEntries, groupBy), [filteredEntries, groupBy]);

  const toggleGroup = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div data-fullwidth="true" style={{ padding: '60px 20px', textAlign: 'center', color: TEXT_MUTED }}>
        Loading journal…
      </div>
    );
  }

  return (
    <div
      data-fullwidth="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '24px 32px 32px',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: 600,
            color: TEXT_TITLE,
            fontStyle: 'italic'
          }}>
            Journal
          </h1>
          <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: '0.9rem' }}>
            Record of work delivered to clients.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: anyFilterActive ? ACCENT : SURFACE,
              color: anyFilterActive ? 'white' : TEXT_MUTED,
              border: anyFilterActive ? 'none' : `1px solid ${BORDER}`,
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <Filter size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: anyFilterActive ? 'rgba(255,255,255,0.3)' : ACCENT,
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: 600
              }}>
                {activeFilterCount}
              </span>
            )}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: ACCENT,
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Plus size={18} /> New Entry
          </button>
        </div>
      </div>

      {/* Search bar (always visible) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '10px'
      }}>
        <Search size={16} color={TEXT_FAINT} />
        <input
          type="search"
          placeholder="Search title or description…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '0.95rem',
            color: TEXT_BODY,
            fontFamily: 'inherit'
          }}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT_FAINT, padding: '4px', display: 'flex' }}
            title="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Collapsible filter panel */}
      {showFilters && (
        <div style={{
          padding: '20px',
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '12px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <div>
              <label style={filterLabelStyle}>
                <Building2 size={12} /> Client
              </label>
              <select
                value={filterClientId}
                onChange={(e) => setFilterClientId(e.target.value)}
                style={fieldStyle(Boolean(filterClientId))}
              >
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label style={filterLabelStyle}>
                <Briefcase size={12} /> Project
              </label>
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                style={fieldStyle(Boolean(filterProjectId))}
              >
                <option value="">All projects</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div>
              <label style={filterLabelStyle}>
                <Calendar size={12} /> From
              </label>
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                style={fieldStyle(Boolean(filterFrom))}
              />
            </div>

            <div>
              <label style={filterLabelStyle}>
                <Calendar size={12} /> To
              </label>
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                style={fieldStyle(Boolean(filterTo))}
              />
            </div>
          </div>

          {anyFilterActive && (
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  background: 'transparent',
                  color: TEXT_MUTED,
                  border: `1px solid ${BORDER}`,
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                <X size={12} /> Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Group-by toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ color: TEXT_MUTED, fontSize: '0.8rem', fontWeight: 500 }}>Group by</span>
        <div style={{
          display: 'inline-flex',
          padding: '3px',
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '999px'
        }}>
          {['month', 'week', 'client'].map((g) => {
            const active = groupBy === g;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                style={{
                  textTransform: 'capitalize',
                  padding: '6px 14px',
                  borderRadius: '999px',
                  border: 'none',
                  background: active ? ACCENT : 'transparent',
                  color: active ? 'white' : TEXT_MUTED,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                {g}
              </button>
            );
          })}
        </div>
        <span style={{ marginLeft: 'auto', color: TEXT_FAINT, fontSize: '0.8rem' }}>
          {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#b91c1c',
          borderRadius: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button
            type="button"
            onClick={loadEntries}
            style={{
              padding: '4px 12px',
              background: 'white',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#b91c1c',
              fontSize: '0.8rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty states */}
      {filteredEntries.length === 0 && entries.length === 0 && !error && (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          background: SURFACE,
          border: `1px dashed ${BORDER}`,
          borderRadius: '12px'
        }}>
          <Award size={48} color={ACCENT} style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: TEXT_TITLE }}>No deliveries logged yet</h3>
          <p style={{ marginTop: '8px', color: TEXT_MUTED, fontSize: '0.9rem' }}>
            Click <strong style={{ color: TEXT_BODY }}>+ New Entry</strong> to record your first win.
          </p>
        </div>
      )}

      {filteredEntries.length === 0 && entries.length > 0 && (
        <div style={{
          padding: '32px 20px',
          textAlign: 'center',
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '12px'
        }}>
          <p style={{ margin: 0, color: TEXT_MUTED, fontSize: '0.95rem' }}>No entries match these filters.</p>
          <button
            type="button"
            onClick={clearFilters}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: ACCENT,
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Grouped entries */}
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {groups.map((group) => {
            const isCollapsed = collapsed.has(group.key);
            return (
              <div key={group.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    padding: '10px 4px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${BORDER}`,
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ChevronRight
                      size={16}
                      color={TEXT_FAINT}
                      style={{ transition: 'transform 0.15s', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
                    />
                    <span style={{ fontSize: '0.95rem', fontWeight: 600, color: TEXT_TITLE }}>{group.label}</span>
                  </div>
                  <span style={{
                    background: SURFACE_MUTED,
                    color: TEXT_MUTED,
                    padding: '2px 10px',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {group.entries.length}
                  </span>
                </button>
                {!isCollapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                    {group.entries.map((e) => (
                      <EntryCard key={e.id} entry={e} user={user} onEdit={handleEdit} onDelete={handleDelete} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(showForm || editing) && (
        <EntryFormModal
          clients={clients}
          projects={projects}
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function EntryCard({ entry, user, onEdit, onDelete }) {
  const canMutate = user?.is_admin || entry.created_by === user?.id;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '14px',
        padding: '18px 20px',
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        transition: 'border-color 0.15s, transform 0.15s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: TEXT_TITLE, lineHeight: 1.35 }}>
          {entry.title}
        </h3>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 10px', alignItems: 'center', marginTop: '8px' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: ACCENT_TINT,
            color: ACCENT,
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            <Building2 size={11} /> {entry.client_name}
          </span>
          {entry.project_name && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: SURFACE_MUTED,
              color: TEXT_MUTED,
              padding: '3px 10px',
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              <Briefcase size={11} /> {entry.project_name}
            </span>
          )}
          <span style={{ color: TEXT_FAINT, fontSize: '0.78rem' }}>
            Delivered {formatDeliveryDate(entry.delivery_date)}
          </span>
          {entry.author_name && (
            <span style={{ color: TEXT_FAINT, fontSize: '0.78rem' }}>
              · {entry.author_name}
            </span>
          )}
        </div>

        <p style={{
          margin: '12px 0 0',
          color: TEXT_BODY,
          fontSize: '0.9rem',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {entry.description}
        </p>
      </div>

      {canMutate && (
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => onEdit(entry)}
            title="Edit"
            style={{
              padding: '8px',
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              color: TEXT_MUTED,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(entry)}
            title="Delete"
            style={{
              padding: '8px',
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              color: '#dc2626',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function EntryFormModal({ clients, projects, onClose, onSaved, initial }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [clientId, setClientId] = useState(initial?.client_id || '');
  const [projectId, setProjectId] = useState(initial?.project_id || '');
  const [deliveryDate, setDeliveryDate] = useState(
    initial?.delivery_date ? String(initial.delivery_date).slice(0, 10) : todayIso()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('Title is required'); return; }
    if (!description.trim()) { setError('Description is required'); return; }
    if (!clientId) { setError('Client is required'); return; }
    if (!deliveryDate) { setError('Delivery date is required'); return; }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        client_id: parseInt(clientId, 10),
        project_id: projectId ? parseInt(projectId, 10) : null,
        delivery_date: deliveryDate
      };
      const res = initial
        ? await axios.put(`/api/journal/${initial.id}`, body)
        : await axios.post('/api/journal', body);
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    fontSize: '0.95rem',
    color: TEXT_BODY,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: SURFACE
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_BODY,
    marginBottom: '6px'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: SURFACE,
        borderRadius: '14px',
        padding: '28px',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: TEXT_TITLE }}>
            {initial ? 'Edit entry' : 'New journal entry'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: TEXT_MUTED,
              padding: '4px',
              display: 'flex'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>
              Title <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. Q2 strategy deck for Nestlé"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Description <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was delivered? Add the context that helps you remember and showcase the work."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>
                Client <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Project (optional)</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">No project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ maxWidth: '260px' }}>
            <label style={labelStyle}>
              Delivered on <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: TEXT_MUTED,
                border: `1px solid ${BORDER}`,
                borderRadius: '24px',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 22px',
                background: saving ? '#a5b4fc' : ACCENT,
                color: 'white',
                border: 'none',
                borderRadius: '24px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving…' : (initial ? 'Save changes' : 'Save entry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Journal;
